import { useState } from "react";
//#region types
import { WintSalaryEnums } from "models/enums";
import { TimeDeviationFormViemProps } from "models/propTypes/wintSalaryPropTypes";
import { ChoiceProps, MonthSliderProps } from "models/propTypes/wintFormPropsTypes";
import { TranslationKeys } from "translations";
import { SalaryDeviationFormTypes } from "models/formTypes";
import { DateRange } from "models/formTypes/util";
//#endregion
//#region components
import { VerticalFormField, DateRangeField, Choice, MonthSlider } from "components/common/wintForms";
import { useWintSalaryDeviationService } from "serviceHooks";
import ReportTimeForm from "./reportTimeForm";
import SalaryDeviationHelper from "./salaryDeviationHelper";
//#endregion
import { DateHelper, LocalizationHelperInstance } from "utils/helpers";
import { StaticInfoMessage } from "components/common";

const TimeDeviationSection: React.FC<TimeDeviationFormViemProps> = (props) => {
  const {
    connect,
    availableDeviationReportTypes,
    values,
    editMode,
    readOnly,
    allDayHoursRef,
    showInfoMessage,
    canManageSalary,
  } = props;
  const {
    getEffectiveDateRange,
    getDailyHoursTemplate,
    convertDailyHoursTemplateToForm,
    generateDefaultDayHours,
    filterDayHoursByMonth,
    mergeDayHoursEdits,
    validateSelectedPeriod,
    mergeEditsIntoDayHours,
    isYearMonthLocked,
  } = useWintSalaryDeviationService();

  const [loadingDayHours, setLoadingDayHours] = useState(false);

  const dateRangeConnector = connect("timeDeviation.dateRange");
  const dayHoursConnector = connect("timeDeviation.dayHours");
  const fillDayHoursConnector = connect("timeDeviation.fillDayHours");
  const reportTypeConnector = connect("timeDeviation.reportType");
  const timeConnector = connect("timeDeviation.time");
  const monthSliderConnector = connect("timeDeviation.selectedPeriod");

  const generateDayHoursData = async (
    from: Date,
    to: Date,
    personId: number,
    isAbsence: boolean,
    time?: { hours: number; minutes: number }
  ): Promise<SalaryDeviationFormTypes.HoursByDayWithMeta[]> => {
    if (isAbsence) {
      const templates = await getDailyHoursTemplate({
        personId,
        startDate: from,
        endDate: to,
      });
      return convertDailyHoursTemplateToForm(templates, undefined, time);
    } else {
      return generateDefaultDayHours(from, to, undefined, time);
    }
  };

  const applyEditModeFiltering = (
    newFormData: SalaryDeviationFormTypes.HoursByDayWithMeta[],
    from: Date,
    to: Date
  ): void => {
    if (!allDayHoursRef) return;

    // Merge any preserved edits from ref into the new data
    const mergedData =
      allDayHoursRef.current.length > 0 ? mergeEditsIntoDayHours(newFormData, allDayHoursRef.current) : newFormData;

    // Store complete list in ref
    allDayHoursRef.current = mergedData;

    // Validate and adjust selected period if needed
    const currentSelectedPeriod = values.timeDeviation?.selectedPeriod ?? from;
    const validatedPeriod = validateSelectedPeriod(currentSelectedPeriod, from, to);

    // Update selected period if it changed
    if (validatedPeriod.getTime() !== currentSelectedPeriod.getTime()) {
      monthSliderConnector.setValue(validatedPeriod);
    }

    // Filter by validated period and set to form
    const filtered = filterDayHoursByMonth(mergedData, validatedPeriod);
    dayHoursConnector.setValue(filtered);
  };

  const onSelectedPeriodChange = (newPeriod: Date) => {
    if (allDayHoursRef) {
      // Merge current month's edits back into the full array
      const currentDayHours = values.timeDeviation?.dayHours ?? [];
      allDayHoursRef.current = mergeDayHoursEdits(allDayHoursRef.current, currentDayHours);

      // Filter for the new month and update form
      const filtered = filterDayHoursByMonth(allDayHoursRef.current, newPeriod);
      dayHoursConnector.setValue(filtered);
    }
    monthSliderConnector.setValue(newPeriod);
  };

  const onDateRangeChange = (range: DateRange) => {
    dateRangeConnector.setValue(range);
    onDateChanged(range.start, range.end, reportTypeConnector.value, undefined, values.timeDeviation?.dayHours);
  };
  const onFillDaysToggleChanged = (value: boolean) => {
    fillDayHoursConnector.setValue(value);

    const rawTime = timeConnector.value;
    let timeValues: { hours: number; minutes: number } | undefined = undefined;

    if (rawTime != null) {
      const hours = typeof rawTime.hours === "string" ? parseInt(rawTime.hours, 10) : rawTime.hours;
      const minutes = typeof rawTime.minutes === "string" ? parseInt(rawTime.minutes, 10) : rawTime.minutes;

      if (Number.isFinite(hours) && Number.isFinite(minutes)) {
        timeValues = { hours, minutes };
      }
    }

    const existingDayHours = value ? [] : values.timeDeviation?.dayHours;

    onDateChanged(
      dateRangeConnector.value?.start,
      dateRangeConnector.value?.end,
      reportTypeConnector.value,
      timeValues,
      existingDayHours
    );
  };
  const onReportTypeChanged = (value: WintSalaryEnums.SalaryDeviationReportType) => {
    reportTypeConnector.setValue(value);
    onDateChanged(
      dateRangeConnector.value?.start,
      dateRangeConnector.value?.end,
      value,
      undefined,
      values.timeDeviation?.dayHours
    );
  };
  const onDateChanged = async (
    startDate?: Date,
    endDate?: Date,
    reportType?: WintSalaryEnums.SalaryDeviationReportType,
    time?: { hours: number; minutes: number },
    existingDayHours?: SalaryDeviationFormTypes.HoursByDayWithMeta[]
  ) => {
    const isTimeReportType = reportType === WintSalaryEnums.SalaryDeviationReportType.Time;
    if (!isTimeReportType || !startDate || !DateHelper.isValidDate(startDate)) return;

    if (endDate && (!DateHelper.isValidDate(endDate) || startDate > endDate)) return;

    const personId = values.employee?.id;
    if (!personId) return;

    const { from, to } = getEffectiveDateRange(startDate, endDate);
    const isAbsence = SalaryDeviationHelper.isAbsenceDeviation(values.deviationType);

    try {
      setLoadingDayHours(true);

      // EDIT MODE: Merge current visible edits back to ref before regenerating
      if (editMode && allDayHoursRef) {
        const currentDayHours = values.timeDeviation?.dayHours ?? [];
        allDayHoursRef.current = mergeDayHoursEdits(allDayHoursRef.current, currentDayHours);
      }

      // Generate fresh dayHours for the new date range
      const newFormData = await generateDayHoursData(from, to, Number(personId), isAbsence, time);

      // Apply edit mode filtering or set directly for create mode
      if (editMode && allDayHoursRef) {
        applyEditModeFiltering(newFormData, from, to);
      } else {
        dayHoursConnector.setValue(newFormData);
      }
    } catch (error) {
      console.error("Failed to fetch daily hours template:", error);
      dayHoursConnector.setValue([]);
    } finally {
      setLoadingDayHours(false);
    }
  };

  const yearMonthStates = values.timeDeviation?.yearMonthStates;
  const isStartDateLocked =
    readOnly || isYearMonthLocked(yearMonthStates, values.timeDeviation?.dateRange?.start, canManageSalary);
  const isEndDateLocked =
    readOnly || isYearMonthLocked(yearMonthStates, values.timeDeviation?.dateRange?.end, canManageSalary);
  const isCurrentMonthLocked =
    readOnly || isYearMonthLocked(yearMonthStates, values.timeDeviation?.selectedPeriod, canManageSalary);

  const defaultFocusedDate = DateHelper.getFirstDayOfPreviousMonth(new Date());
  const startDateInitialFocus =
    dateRangeConnector.value?.end instanceof Date ? dateRangeConnector.value.end : defaultFocusedDate;
  const endDateInitialFocus =
    dateRangeConnector.value?.start instanceof Date ? dateRangeConnector.value.start : defaultFocusedDate;

  // Vacation deviations are always reported as full days - hide the FullDay/Time choice
  const isVacation = SalaryDeviationHelper.isVacationClassification(values.deviation);

  return (
    <>
      <DateRangeField
        name="timeDeviation.dateRange"
        value={dateRangeConnector.value}
        onChange={dateRangeConnector.onChange}
        onBlur={dateRangeConnector.onBlur}
        setValue={onDateRangeChange}
        setTouched={dateRangeConnector.setTouched}
        touched={dateRangeConnector.touched}
        hasError={dateRangeConnector.hasError}
        error={dateRangeConnector.error}
        label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.SelectDate)}
        startDateLabel={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.StartDate)}
        endDateLabel={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.EndDate)}
        startDateInitialFocus={startDateInitialFocus}
        endDateInitialFocus={endDateInitialFocus}
        startDisabled={isStartDateLocked}
        endDisabled={isEndDateLocked}
      />
      {editMode && (
        <VerticalFormField<MonthSliderProps>
          Component={MonthSlider}
          {...monthSliderConnector}
          setValue={onSelectedPeriodChange}
          maxMonth={values.timeDeviation?.dateRange?.end}
          minMonth={values.timeDeviation?.dateRange?.start}
          shortAdjacentFormat={false}
          shortFormat={false}
          disabled={loadingDayHours}
        />
      )}
      {!editMode && !isVacation && (
        <VerticalFormField<ChoiceProps>
          Component={Choice}
          {...reportTypeConnector}
          setValue={onReportTypeChanged}
          label={TranslationKeys.SalaryDeviation.DeviationTypeLabel}
          showMainLabel
          items={availableDeviationReportTypes}
        />
      )}
      {showInfoMessage && (
        <StaticInfoMessage gutterBottom>
          {LocalizationHelperInstance.getFormattedMessage(
            TranslationKeys.SalaryDeviation.LockedPeriodReApprovalWarning
          )}
        </StaticInfoMessage>
      )}
      {values.timeDeviation?.reportType === WintSalaryEnums.SalaryDeviationReportType.Time && (
        <ReportTimeForm
          connect={connect}
          values={values}
          onFillDaysToggleChanged={onFillDaysToggleChanged}
          loading={loadingDayHours}
          editMode={editMode}
          isCurrentMonthApproved={isCurrentMonthLocked}
        />
      )}
    </>
  );
};

export default TimeDeviationSection;
